import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Button,
  Select,
  DatePicker,
  Space,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { setFilters } from "../CrmReportSlice";
import { fetchCrmReport } from "../CrmReportThunk";

import {
  fetchLeadSourceApi,
  fetchLeadStatusApi,
  fetchLeadPurposeApi,
} from "../../../apiservice/crm";

const CrmReportFilters = ({ messageApi }) => {
  const dispatch = useDispatch();

  const { filters } = useSelector((state) => state.crmReport);

  const [sourceList, setSourceList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [purposeList, setPurposeList] = useState([]);

  useEffect(() => {
    loadMasters();
  }, []);

  const loadMasters = async () => {
    try {
      const [sourceRes, statusRes, purposeRes] = await Promise.all([
        fetchLeadSourceApi(),
        fetchLeadStatusApi(),
        fetchLeadPurposeApi(),
      ]);

      setSourceList(sourceRes?.data || []);
      setStatusList(statusRes?.data || []);
      setPurposeList(purposeRes?.data || []);
    } catch (err) {
      console.log("Failed to load CRM report masters", err);
    }
  };

  const handleFilterChange = (field, value) => {
    dispatch(
      setFilters({
        [field]: value,
      })
    );
  };

  const handleReportTypeChange = (value) => {
    dispatch(
      setFilters({
        reportType: value,

        // Clear combined-only filters when switching report type
        source_id: null,
        status_id: null,
        purpose_id: null,
      })
    );
  };

  const setQuickRange = (type) => {
    let fromDate;
    let toDate;

    if (type === "today") {
      fromDate = dayjs().startOf("day");
      toDate = dayjs().endOf("day");
    }

    if (type === "last7days") {
      fromDate = dayjs().subtract(6, "day").startOf("day");
      toDate = dayjs().endOf("day");
    }

    if (type === "thismonth") {
      fromDate = dayjs().startOf("month");
      toDate = dayjs().endOf("month");
    }

    dispatch(
      setFilters({
        fromDate,
        toDate,
      })
    );
  };

  const handleReport = () => {
    if (!filters.reportType) {
      messageApi.warning("Select Report Type");
      return;
    }

    if (!filters.fromDate || !filters.toDate) {
      messageApi.warning("Select From Date and To Date");
      return;
    }

    if (dayjs(filters.fromDate).isAfter(dayjs(filters.toDate))) {
      messageApi.warning("From Date cannot be after To Date");
      return;
    }

    dispatch(fetchCrmReport(filters));
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} align="bottom">

        {/* REPORT TYPE */}
        <Col xs={24} sm={12} md={8} lg={5}>
          <label>Report Type</label>

          <Select
            value={filters.reportType}
            onChange={handleReportTypeChange}
            style={{ width: "100%" }}
            size="large"
            options={[
              {
                label: "Source-wise Report",
                value: "source",
              },
              {
                label: "Status-wise Report",
                value: "status",
              },
              {
                label: "Source + Status + Purpose Report",
                value: "combined",
              },
            ]}
          />
        </Col>

        {/* COMBINED REPORT FILTERS */}
        {filters.reportType === "combined" && (
          <>
            <Col xs={24} sm={12} md={8} lg={4}>
              <label>Source</label>

              <Select
                placeholder="All Sources"
                allowClear
                value={filters.source_id || undefined}
                onChange={(value) =>
                  handleFilterChange("source_id", value || null)
                }
                style={{ width: "100%" }}
                size="large"
                options={sourceList.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <label>Status</label>

              <Select
                placeholder="All Statuses"
                allowClear
                value={filters.status_id || undefined}
                onChange={(value) =>
                  handleFilterChange("status_id", value || null)
                }
                style={{ width: "100%" }}
                size="large"
                options={statusList.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <label>Purpose</label>

              <Select
                placeholder="All Purposes"
                allowClear
                value={filters.purpose_id || undefined}
                onChange={(value) =>
                  handleFilterChange("purpose_id", value || null)
                }
                style={{ width: "100%" }}
                size="large"
                options={purposeList.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Col>
          </>
        )}

        {/* FROM DATE */}
        <Col xs={24} sm={12} md={8} lg={4}>
          <label>
            <span style={{ color: "red" }}>* </span>
            From Date
          </label>

          <DatePicker
            value={filters.fromDate ? dayjs(filters.fromDate) : null}
            onChange={(date) =>
              handleFilterChange("fromDate", date)
            }
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            size="large"
          />
        </Col>

        {/* TO DATE */}
        <Col xs={24} sm={12} md={8} lg={4}>
          <label>
            <span style={{ color: "red" }}>* </span>
            To Date
          </label>

          <DatePicker
            value={filters.toDate ? dayjs(filters.toDate) : null}
            onChange={(date) =>
              handleFilterChange("toDate", date)
            }
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            size="large"
          />
        </Col>

        {/* QUICK RANGE */}
        <Col xs={24} sm={12} md={8} lg={5}>
          <label>Quick Range</label>

          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Button
              block
              onClick={() => setQuickRange("today")}
            >
              Today
            </Button>

            <Button
              block
              onClick={() => setQuickRange("last7days")}
            >
              Last 7 Days
            </Button>

            <Button
              block
              onClick={() => setQuickRange("thismonth")}
            >
              This Month
            </Button>
          </Space>
        </Col>

        {/* REPORT BUTTON */}
        <Col xs={24} sm={12} md={8} lg={4}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            size="large"
            block
          >
            Report
          </Button>
        </Col>

      </Row>
    </Card>
  );
};

export default CrmReportFilters;